import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import "dayjs/locale/es";
import { CITY_NAMES, DEPARTAMENTOS, ESTADOS, REGLAS_ESTADO, VENTANA_RAFAGA_MS } from "./notificacionesConfig";

dayjs.extend(isoWeek);

const normalizarTexto = (s) =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// Traduce el nombre del estado (de BD o del texto de audiencia) a una clave de
// ESTADOS. Lo que no coincide cae en "sin_estado" (chip gris neutro).
export const claveEstado = (nombre) => {
  const texto = normalizarTexto(nombre);
  if (!texto) return "sin_estado";
  const regla = REGLAS_ESTADO.find((r) => r.frases.some((f) => texto.includes(f)));
  return regla ? regla.clave : "sin_estado";
};

export const estadoVisual = (nombre) => {
  const clave = claveEstado(nombre);
  const base = ESTADOS[clave] || ESTADOS.sin_estado;
  return { clave, ...base, label: nombre || base.label };
};

const quitarPuntoFinal = (s) => (s || "").replace(/\.\s*$/, "");

const quitarNombreInicial = (texto, nombre) => {
  if (!texto || !nombre) return texto || "";
  return texto.startsWith(nombre) ? texto.slice(nombre.length).trim() : texto;
};

const capitalizar = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Nombre propio con mayúscula inicial en cada palabra (nombre y apellidos),
// para mostrar en pantalla sin alterar el texto crudo usado para comparar
// contra la descripción original (misma longitud, solo cambia la mayúscula).
export const nombrePropio = (s) =>
  (s || "")
    .toLowerCase()
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");

// Clasifica el texto del evento para elegir el ícono de la fila de ráfaga.
export const tipoEvento = (texto) => {
  const t = normalizarTexto(texto);
  if (t.includes("elimin")) return "eliminacion";
  if (/agend|creo|registr|program/.test(t)) return "alta";
  return "edicion";
};

// Convierte cada fila del API en un objeto listo para la vista.
export const construirItems = (apiItems, userId) => {
  const usuarioActual = Number(userId);
  return (apiItems || []).map((x) => {
    const yaLeyeron = Array.isArray(x?.usuarios_leyeron) ? x.usuarios_leyeron : [];
    const leido = yaLeyeron.some((u) => Number(u?.id_user) === usuarioActual);
    const departamento = DEPARTAMENTOS[x.departamento] ? x.departamento : "conciliacion";
    const idKey = x.id_key != null ? Number(x.id_key) : null;
    const ruta = idKey != null ? DEPARTAMENTOS[departamento].ruta(idKey) : null;
    const estado = estadoVisual(x.estado_nombre);

    return {
      id: `n${x.id}`,
      db_id: x.id,
      fecha: new Date(String(x.fecha_notificacion || x.datetime).replace(" ", "T")),
      categoria: x.categoria || "otro",
      departamento,
      nombreModulo: DEPARTAMENTOS[departamento].label,
      icono: DEPARTAMENTOS[departamento].icon,
      ciudadId: x.ciudad_id != null ? Number(x.ciudad_id) : null,
      ciudadNombre: x.ciudad_id != null ? CITY_NAMES[Number(x.ciudad_id)] || null : null,
      idKey,
      ruta,
      expediente: x.expediente || null,
      descripcion: x.descripcion || "",
      nombreUsuario: x.nombre_usuario || null,
      idUsuario: x.id_user != null ? Number(x.id_user) : null,
      urgente: x.urgente === 1,
      leido,
      estado,
    };
  });
};

// Agrupa eventos consecutivos (lista ya ordenada de más reciente a más antigua)
// del mismo usuario sobre el mismo expediente, separados por menos de
// VENTANA_RAFAGA_MS. Devuelve entradas: { tipo: "simple" | "rafaga", items }.
export const agruparRafagas = (items) => {
  const entradas = [];
  let actual = null;

  items.forEach((item) => {
    const clave =
      item.idKey != null && item.idUsuario != null ? `${item.idUsuario}|${item.categoria}|${item.idKey}` : null;

    const continua =
      actual &&
      clave &&
      actual.clave === clave &&
      actual.ultimo.fecha - item.fecha <= VENTANA_RAFAGA_MS;

    if (continua) {
      actual.items.push(item);
      actual.ultimo = item;
      return;
    }

    actual = { clave, items: [item], ultimo: item };
    entradas.push(actual);
  });

  return entradas.map((e) =>
    e.items.length > 1 && e.clave
      ? { tipo: "rafaga", key: `r${e.items[0].db_id}`, items: e.items }
      : { tipo: "simple", key: `n${e.items[0].db_id}`, items: e.items }
  );
};

const capitalizarFrase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Etiqueta de grupo por fecha: día de la semana + fecha, igual que en Agenda
// (p. ej. "Miércoles 28 de octubre"). Solo lleva año cuando no es el actual.
export const etiquetaFecha = (fecha, ahora = new Date()) => {
  const d = dayjs(fecha).locale("es");
  const base = capitalizarFrase(d.format("dddd D [de] MMMM"));
  return d.year() === dayjs(ahora).year() ? base : `${base} de ${d.format("YYYY")}`;
};

// true si `fecha` cae dentro del rango rápido seleccionado (ver RANGOS_FECHA).
// `personalizado` es [dayjs, dayjs] cuando modo === "personalizado".
export const dentroDeRangoFecha = (fecha, modo, personalizado, ahora = new Date()) => {
  if (!modo || modo === "todas") return true;
  const d = dayjs(fecha);
  const hoy = dayjs(ahora);

  if (modo === "hoy") return d.isSame(hoy, "day");
  if (modo === "semana") return d.isoWeek() === hoy.isoWeek() && d.year() === hoy.year();
  if (modo === "mes") return d.isSame(hoy, "month");
  if (modo === "personalizado") {
    if (!personalizado || !personalizado[0] || !personalizado[1]) return true;
    const [inicio, fin] = personalizado;
    return (
      (d.isSame(inicio, "day") || d.isAfter(inicio, "day")) &&
      (d.isSame(fin, "day") || d.isBefore(fin, "day"))
    );
  }
  return true;
};

// Agrupa entradas (ya ordenadas) en secciones consecutivas por etiqueta de fecha.
export const agruparPorFecha = (entradas, ahora = new Date()) => {
  const grupos = [];
  entradas.forEach((entrada) => {
    const etiqueta = etiquetaFecha(entrada.items[0].fecha, ahora);
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.etiqueta === etiqueta) {
      ultimo.entradas.push(entrada);
    } else {
      grupos.push({ etiqueta, entradas: [entrada] });
    }
  });
  return grupos;
};

// Texto principal de una notificación: sin punto final, con el nombre del
// usuario en negrita cuando el texto ya lo trae al inicio.
export const textoPrincipal = (item) => quitarPuntoFinal(item.descripcion);

// Texto de un evento dentro de una ráfaga, sin repetir el nombre del usuario.
export const textoEvento = (item) =>
  capitalizar(quitarPuntoFinal(quitarNombreInicial(item.descripcion, item.nombreUsuario)));

export const iniciaConNombre = (item) =>
  Boolean(item.nombreUsuario && item.descripcion && item.descripcion.startsWith(item.nombreUsuario));
